// One client for both hosting modes: the same events, the same calls, whether the kit runs
// in-process (tauri-plugin-voice-kit) or as the standalone server (WebSocket). It also runs the
// page's audio for `audio:"client"` sessions (webview or browser mic and speakers) and keeps a
// playback position for word-by-word highlighting in every session kind.

import type { Capture } from "./capture";
import { ClockPlayer, type Player } from "./player";
import {
  decodeAudioDown,
  type ClientFrame,
  type Feature,
  type Id,
  type KitState,
  type ServerMsg,
  type ServerMsgOf,
  type ServerMsgType,
  type SessionAudio,
} from "./protocol";
import type { Position } from "./scheduler";
import type { Transport, TransportStatus } from "./transport";

export interface ClientOptions {
  transport: Transport;
  /** Plays TTS audio in the page for `audio:"client"` sessions. */
  player?: Player;
  /** Captures the mic in the page for `audio:"client"` sessions. */
  capture?: Capture;
}

export interface StartOptions {
  /** `"local"`: the kit's own mic and speakers; `"client"`: this page's; `"none"`: typed only. */
  audio?: SessionAudio;
  speakerProfile?: string;
  /** The session's current conversation (protocol 1.3; needs the `conversations` feature). */
  conversationId?: string;
  /**
   * `audio:"client"` only: `false` plays the replies in the page without opening the mic (a
   * typed turn that should still be heard). The kit needs nothing new for it: a client session
   * whose mic sends nothing just never hears speech. {@link listen} opens the mic later, in the
   * same session. Default `true`.
   */
  mic?: boolean;
}

/** A file to upload (protocol 1.5, feature `attachments`). */
export interface Upload {
  /** This client's id for it (unique per connection; reuse it to send it again after a reconnect). */
  id: string;
  name: string;
  mime: string;
  bytes: Uint8Array;
  /** A small `data:image/...` preview, shown in transcripts. */
  thumb?: string;
}

/** Bytes per `attachment.put` chunk: well under the relay's 1 MiB frame limit once in base64. */
export const UPLOAD_CHUNK_BYTES = 192 * 1024;

type Listener<T extends ServerMsgType> = (msg: ServerMsgOf<T>) => void;

export class VoiceKitClient {
  readonly transport: Transport;
  /** The kit's state (`idle` when no session). */
  state: KitState = "idle";
  sessionId: Id | null = null;
  sessionAudio: SessionAudio | null = null;
  /** Changes when the kit process restarts (ids start over): views reset on it. */
  epoch: string | null = null;
  welcome: ServerMsgOf<"welcome"> | null = null;
  status: TransportStatus = "closed";

  readonly player: Player | null;
  readonly capture: Capture | null;
  private readonly clock = new ClockPlayer();
  private readonly listeners = new Map<string, Set<(m: ServerMsg) => void>>();
  private readonly anyListeners = new Set<(m: ServerMsg) => void>();
  private readonly statusListeners = new Set<(s: TransportStatus) => void>();
  /** Recently silenced replies (the latest SILENCED_MAX), whose late audio frames are dropped. */
  private readonly silenced = new Set<Id>();
  /** Replies started and not yet heard: marked unspoken if the connection drops. */
  private readonly openReplies = new Set<Id>();
  /** Why the kit refused this client for good (bad token, protocol error), if it did. */
  fatalError: ClientError | null = null;
  /**
   * The reply that is paused (protocol 1.9): by this client ({@link pauseReply}) or another
   * (the kit's `reply.paused`). `null` when nothing is paused.
   */
  pausedReply: Id | null = null;
  /** The reply whose audio has started and whose voice has not ended (`tts.chunk` to `reply.heard`). */
  private chunkedReply: Id | null = null;
  private micLive = false;
  /** Until this time (performance.now()), mic frames go out as silence (see muteMicFor). */
  private mutedUntil = 0;
  /** A `session.start` of this client's awaits its `session.started`. */
  private claiming = false;
  private ownSessionId: Id | null = null;
  private nextId = 1;
  private readonly pending = new Map<string, { resolve: () => void; reject: (e: ClientError) => void }>();
  private readonly clockTimer: ReturnType<typeof setInterval>;

  constructor(opts: ClientOptions) {
    this.transport = opts.transport;
    this.player = opts.player ?? null;
    this.capture = opts.capture ?? null;
    this.player?.onProgress((id, ms) => this.transport.send({ type: "playback.progress", reply_id: id, ms }));
    this.player?.onDone((id, ms) => this.transport.send({ type: "playback.done", reply_id: id, ms }));
    this.clockTimer = setInterval(() => this.clock.tick(), 100);
  }

  /**
   * Connect the transport. Resolves on the first `welcome`; rejects if the kit refuses this
   * client for good (`unauthorized`, `protocol_error`), which also stops reconnecting.
   */
  connect(): Promise<ServerMsgOf<"welcome">> {
    this.fatalError = null;
    let fail: (e: ClientError) => void = () => {};
    const first = new Promise<ServerMsgOf<"welcome">>((resolve, reject) => {
      fail = reject;
      const off = this.on("welcome", (w) => {
        off();
        resolve(w);
      });
    });
    this.transport.connect({
      onMessage: (m) => this.receive(m),
      onStatus: (s) => {
        this.status = s;
        if (s === "closed") this.connectionLost();
        for (const fn of this.statusListeners) fn(s);
      },
      onFatal: (code, message) => {
        this.fatalError = new ClientError(code, message);
        fail(this.fatalError);
      },
    });
    return first;
  }

  close(): void {
    clearInterval(this.clockTimer);
    this.stopAudio();
    this.transport.close();
  }

  // -- events --------------------------------------------------------------------------------

  /** Listen to one event type. Returns an unsubscribe function. */
  on<T extends ServerMsgType>(type: T, fn: Listener<T>): () => void {
    let set = this.listeners.get(type);
    if (!set) this.listeners.set(type, (set = new Set()));
    set.add(fn as (m: ServerMsg) => void);
    return () => set!.delete(fn as (m: ServerMsg) => void);
  }

  /** Every event (feed models with it: `client.onAny((m) => transcript.apply(m))`). */
  onAny(fn: (msg: ServerMsg) => void): () => void {
    this.anyListeners.add(fn);
    return () => this.anyListeners.delete(fn);
  }

  onStatus(fn: (s: TransportStatus) => void): () => void {
    this.statusListeners.add(fn);
    return () => this.statusListeners.delete(fn);
  }

  // -- calls ---------------------------------------------------------------------------------

  /**
   * Start a voice session. For `audio:"client"` the page's mic starts first (inside the tap on
   * iOS: call `prepareAudio()` synchronously from the click handler), so a mic that never
   * delivers never starts a session.
   */
  async startSession(opts: StartOptions = {}): Promise<void> {
    const audio = opts.audio ?? (this.capture && this.player ? "client" : undefined);
    const mic = audio === "client" && opts.mic !== false;
    if (audio === "client") {
      if (!this.player || (mic && !this.capture)) throw new ClientError("bad_message", "client audio needs a player and a capture");
      if (mic) {
        this.prepareAudio();
        await this.startCapture();
      } else this.unlockPlayback();
    }
    // The next `session.started` is this client's (it may come before or after the ack).
    this.claiming = true;
    try {
      await this.request({
        type: "session.start",
        audio,
        speaker_profile: opts.speakerProfile,
        ...(opts.conversationId !== undefined ? { conversation_id: opts.conversationId } : {}),
      });
      if (mic) this.micLive = true;
    } catch (e) {
      this.claiming = false;
      if (mic) this.capture?.stop();
      throw e;
    }
  }

  /**
   * Open the mic in this client's own `audio:"client"` session started without it
   * (`startSession({ audio: "client", mic: false })`). iOS: call `prepareAudio()` inside the tap
   * first. A no-op when the mic is already live.
   */
  async listen(): Promise<void> {
    if (this.micLive) return;
    if (!this.ownsSession || this.sessionAudio !== "client") throw new ClientError("no_session", "no client-audio session of this client's to listen in");
    if (!this.capture) throw new ClientError("bad_message", "listening needs a capture");
    const id = this.sessionId;
    await this.startCapture();
    // The session may have ended while the mic was waking.
    if (this.sessionId !== id || !this.ownsSession) {
      this.capture.stop();
      throw new ClientError("no_session", "the session ended");
    }
    this.micLive = true;
  }

  /**
   * Fetch the page's mic again in place, in this tap (after a call iOS can leave it deaf): a
   * fresh track and audio context; the session goes on. iOS: call it synchronously inside the
   * tap. Opens the mic if it wasn't.
   */
  reopenMic(): Promise<void> {
    if (this.micLive) {
      this.micLive = false;
      this.capture?.stop();
    }
    return this.listen();
  }

  /** The page's mic is sending to the kit (false in a session started with `mic: false`). */
  get listening(): boolean {
    return this.micLive;
  }

  /**
   * The page's mic is really capturing for the kit right now: it is sending, and the capture
   * says audio is arriving (`Capture.live`). What a "Mic on" indicator should show; the
   * session's state alone can't tell (a typed turn's session, a stalled mic).
   */
  get micCapturing(): boolean {
    return this.micLive && !!this.capture && (this.capture.live?.() ?? true);
  }

  /**
   * For the next `ms`, mic frames go to the kit as silence (same length, so its timing holds):
   * the page's own short sounds (an earcon as the mic opens) can't reach its VAD or barge-in,
   * whatever the echo canceller lets through.
   */
  muteMicFor(ms: number): void {
    this.mutedUntil = Math.max(this.mutedUntil, performance.now() + ms);
  }

  private startCapture(): Promise<void> {
    return this.capture!.start((pcm) => {
      if (!this.micLive) return;
      this.transport.sendAudio(this.mutedUntil && performance.now() < this.mutedUntil ? new Int16Array(pcm.length) : pcm);
    });
  }

  /**
   * This client started the running session. False when there is no session, or when the
   * session is another client's (the PC's own, another phone): `sessionId` alone can't tell,
   * since `welcome` and `session.started` report the kit's session whoever runs it.
   */
  get ownsSession(): boolean {
    return this.ownSessionId !== null && this.ownSessionId === this.sessionId;
  }

  /** iOS: call synchronously inside the user's tap, before anything is awaited. */
  prepareAudio(): void {
    this.player?.unlock?.();
    this.capture?.prepare?.();
  }

  /**
   * iOS: unlock the page's speaker only (no mic asked for), synchronously inside the user's tap,
   * for replies to a typed turn.
   */
  unlockPlayback(): void {
    this.player?.unlock?.();
  }

  /** Ends the session. Only silences playback; never cancels the brain's work. */
  stopSession(reason = "user"): Promise<void> {
    this.stopAudio();
    return this.request({ type: "session.stop", reason });
  }

  /**
   * A typed turn. `attachments`: ids of finished uploads ({@link upload}) to send with it
   * (protocol 1.5); `text` may then be empty.
   */
  say(text: string, opts: { conversationId?: string; attachments?: string[] } = {}): Promise<void> {
    const conv = opts.conversationId !== undefined ? { conversation_id: opts.conversationId } : {};
    const files = opts.attachments?.length ? { attachments: opts.attachments } : {};
    return this.request({ type: "user.text", text, ...conv, ...files });
  }

  /** The kit supports `feature` for this client (it said so in `welcome.features`). */
  supports(feature: Feature): boolean {
    return this.welcome?.features?.includes(feature) ?? false;
  }

  /**
   * Upload a file in `attachment.put` chunks, one at a time, each acknowledged before the next
   * (protocol 1.5). Resolves when the kit has all of it; `onProgress` gets 0..1. `cancelled()`
   * is checked between chunks (a rejection with code `cancelled` follows).
   */
  async upload(file: Upload, onProgress?: (fraction: number) => void, cancelled?: () => boolean): Promise<void> {
    const size = file.bytes.length;
    let offset = 0;
    do {
      if (cancelled?.()) throw new ClientError("cancelled", "upload cancelled");
      const end = Math.min(size, offset + UPLOAD_CHUNK_BYTES);
      await this.request({
        type: "attachment.put",
        attachment_id: file.id,
        name: file.name,
        mime: file.mime,
        size,
        offset,
        data: base64(file.bytes.subarray(offset, end)),
        ...(offset === 0 && file.thumb ? { thumb: file.thumb } : {}),
      });
      offset = end;
      onProgress?.(size ? offset / size : 1);
    } while (offset < size);
  }

  /**
   * The uploads that go with the next turn, spoken or typed (protocol 1.5). Replaces the
   * staged set; `[]` clears it. Finished uploads not listed (and not staged before) are dropped.
   */
  stageAttachments(ids: string[]): Promise<void> {
    return this.request({ type: "attachment.stage", attachment_ids: ids });
  }

  /**
   * Set (or with `null`, clear) the session's current conversation: later turns carry it.
   * Protocol 1.3; the transport must declare the `conversations` feature.
   */
  setConversation(conversationId: string | null): Promise<void> {
    return this.request({ type: "session.conversation", conversation_id: conversationId });
  }

  /** Tap to stop: silences the voiced reply. Never cancels work. */
  silence(replyId?: Id): Promise<void> {
    return this.request({ type: "reply.silence", reply_id: replyId });
  }

  /**
   * Kit 0.13 (issue #18): the voiced reply has played what it was given and waits for more
   * (`thinking` with its voice still open). A tap that would pause it while speaking pauses it
   * here too.
   */
  get betweenChunks(): boolean {
    return this.state === "thinking" && this.chunkedReply !== null;
  }

  /** A reply is paused (see {@link pauseReply}). */
  get paused(): boolean {
    return this.pausedReply !== null;
  }

  /**
   * Pause the reply being spoken where it is: a tap on the orb, a phone call (protocol 1.9).
   * Not an interruption: nothing is reported as heard, the rest of the reply is kept (audio
   * that still arrives is kept too) and the brain's work goes on. Audio played in this page
   * pauses at once, even offline; the kit is told when it supports `pause` (it pauses its own
   * speakers, and tells every UI). {@link resumeReply} carries on from the same spot; a new
   * spoken turn drops the rest instead (typing keeps it). Call it synchronously inside the tap on
   * iOS. Returns false when there is nothing to pause, or it can't be paused (an older kit
   * playing on its own speakers): the caller may silence instead.
   */
  pauseReply(): boolean {
    if (this.pausedReply !== null) return true;
    const kit = this.supports("pause");
    // In a silent gap of an open reply (`thinking`: before its first chunk, or played out with
    // more to come) nothing plays here to pause, so the kit holds it (issue #18): its
    // `reply.paused` names the reply, and this page's audio follows that. An older kit can't.
    if (this.state === "thinking") {
      if (!kit) return false;
      void this.request({ type: "reply.pause" }).catch(() => {});
      return true;
    }
    if (this.state !== "speaking") return false;
    const inPage = this.sessionAudio === "client" && !!this.player;
    // The kit's own speakers (or its virtual clock) only pause if the kit can do it.
    if (!inPage && !kit) return false;
    const here = inPage ? this.player! : this.clock;
    const pos = here.pause?.() ?? null;
    if (!pos && !kit) return false;
    if (pos) this.setPaused(pos.replyId, true, pos.msPlayed);
    if (kit) void this.request({ type: "reply.pause", ...(pos ? { reply_id: pos.replyId } : {}) }).catch(() => {});
    return true;
  }

  /**
   * Carry on speaking the paused reply from where it stopped. iOS: call it synchronously inside
   * the tap (after `prepareAudio()` if the page was in the background). Returns false if
   * nothing is paused.
   */
  resumeReply(): boolean {
    const id = this.pausedReply;
    if (id === null) return false;
    const here = this.sessionAudio === "client" && this.player ? this.player : this.clock;
    here.resume?.(id);
    this.setPaused(id, false, here.progressNow()?.msPlayed ?? 0);
    if (this.supports("pause")) void this.request({ type: "reply.resume", reply_id: id }).catch(() => {});
    return true;
  }

  /** Pause or resume, whichever applies: a tap or click on the orb while speaking. */
  togglePause(): boolean {
    return this.pausedReply !== null ? this.resumeReply() : this.pauseReply();
  }

  private setPaused(replyId: Id, paused: boolean, ms: number): void {
    const was = this.pausedReply;
    this.pausedReply = paused ? replyId : null;
    // Views follow `reply.paused`; with a kit that doesn't send it, the client says so itself.
    if ((was === replyId) !== paused) this.emit({ type: "reply.paused", reply_id: replyId, paused, ms: Math.round(ms) });
  }

  /** Explicitly cancel the brain's work (all of it, or one reply). */
  cancelWork(replyId?: Id): Promise<void> {
    return this.request({ type: "work.cancel", reply_id: replyId });
  }

  /**
   * Learn the voice of `profile` on the session's mic (1.6: `source` names another mic). It adds
   * to the profile; the mic's earlier learning is replaced, other mics' is kept. A phone's
   * profile is always its own mic, whatever it sends.
   */
  enrollStart(profile: string, source?: string): Promise<void> {
    return this.request({ type: "speaker.enroll.start", profile, ...(source ? { source } : {}) });
  }

  /** Forget a voice profile, or (1.6) only its learning on the mic `source`. */
  speakerForget(profile: string, source?: string): Promise<void> {
    return this.request({ type: "speaker.forget", profile, ...(source ? { source } : {}) });
  }

  /**
   * 1.8, `guests`: name a guest of the running session (`name` empty: back to "Guest n"). `save`
   * remembers their voice so they are recognised next time: biometric data, so only on the
   * user's explicit choice, with the guest's OK (the guests panel asks).
   */
  guestRename(guestId: string, name: string, save = false): Promise<void> {
    const n = name.trim();
    return this.request({ type: "guest.rename", guest_id: guestId, ...(n ? { name: n } : {}), ...(save ? { save } : {}) });
  }

  /** 1.8: forget a guest of the running session (and their saved voice, if they had one). */
  guestForget(guestId: string): Promise<void> {
    return this.request({ type: "guest.forget", guest_id: guestId });
  }

  /** 1.8: delete a saved guest's voice by name (settings). */
  guestForgetSaved(name: string): Promise<void> {
    return this.request({ type: "guest.forget", name });
  }

  /** 1.8: ask for a `guest.list` event. */
  guestsGet(): Promise<void> {
    return this.request({ type: "guests.get" });
  }

  /**
   * 1.10, `speech_mine`: "That was me": the ignored speech `ignoredId` was the user's. The kit
   * answers every UI with `speech.reattributed` (moved, sent to the assistant once, and learned
   * only if it sounded close enough to their voice).
   */
  speechMine(ignoredId: string): Promise<void> {
    return this.request({ type: "speech.mine", ignored_id: ignoredId });
  }

  /**
   * 1.10, kit 0.12: send a line marked "That was me" that was not sent on its own (it was not
   * the latest thing the user said) to the assistant now, once. `speech.mine.sent` confirms it.
   */
  speechMineSend(ignoredId: string): Promise<void> {
    return this.request({ type: "speech.mine.send", ignored_id: ignoredId });
  }

  /** 1.10: undo the learning of one "That was me" (`mark_id` from `speech.marks`). */
  speechUnlearn(markId: string): Promise<void> {
    return this.request({ type: "speech.unlearn", mark_id: markId });
  }

  /** 1.10: ask for a `speech.marks` event (the "That was me" history). */
  speechMarksGet(): Promise<void> {
    return this.request({ type: "speech.marks.get" });
  }

  /**
   * 1.10: the audio of the ignored speech `ignoredId` (16 kHz mono PCM16), to play it back. The
   * kit mutes its mic for the clip's length meanwhile. Rejects with `ClientError` code
   * `clip_expired` once the kit no longer keeps it (10 minutes at most; it outlives voice ending,
   * not the app closing).
   */
  /**
   * 1.10: the clip `ignoredId` started (`playing`) or stopped playing on this page's speaker.
   * When this page runs the session's mic, the kit hears the mic as silence while it plays (a
   * stop ends that at once); otherwise it changes nothing.
   */
  speechClipPlaying(ignoredId: string, playing: boolean): Promise<void> {
    return this.request({ type: "speech.clip.playing", ignored_id: ignoredId, playing });
  }

  async speechClip(ignoredId: string): Promise<Int16Array> {
    const parts: Array<{ offset: number; bytes: Uint8Array }> = [];
    let size = 0;
    const off = this.on("speech.clip", (m) => {
      if (m.ignored_id !== ignoredId) return;
      size = m.size;
      const bin = atob(m.data);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      parts.push({ offset: m.offset, bytes });
    });
    try {
      await this.request({ type: "speech.clip.get", ignored_id: ignoredId });
    } finally {
      off();
    }
    const all = new Uint8Array(size);
    for (const p of parts) all.set(p.bytes.subarray(0, Math.max(0, size - p.offset)), p.offset);
    const pcm = new Int16Array(size >> 1);
    const view = new DataView(all.buffer);
    for (let i = 0; i < pcm.length; i++) pcm[i] = view.getInt16(i * 2, true);
    return pcm;
  }

  enrollStop(save = true): Promise<void> {
    return this.request({ type: "speaker.enroll.stop", save });
  }

  setSpeakerVerification(enabled: boolean): Promise<void> {
    return this.request({ type: "speaker.verify", enabled });
  }

  /** Send any protocol message; resolves on its `ack`, rejects with its `error`. */
  request(msg: ClientFrame): Promise<void> {
    const id = `c${this.nextId++}`;
    return new Promise<void>((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      this.transport.send({ ...msg, id });
    });
  }

  /** Where the voiced reply's audio is right now (page audio, or the kit's, estimated). */
  progressNow(): Position | null {
    return this.sessionAudio === "client" && this.player ? this.player.progressNow() : this.clock.progressNow();
  }

  /** Output (speaking) or mic (listening) level 0..1, for the orb. */
  level(): number {
    if (this.state === "speaking") return this.sessionAudio === "client" && this.player ? this.player.level() : this.clock.level();
    return this.micLive && this.capture ? this.capture.level() : 0;
  }

  // -- incoming ------------------------------------------------------------------------------

  private receive(m: ServerMsg | ArrayBuffer): void {
    if (m instanceof ArrayBuffer) return this.audio(m);
    switch (m.type) {
      case "welcome":
        if (this.epoch !== null && this.epoch !== m.epoch) this.newEpoch();
        this.epoch = m.epoch;
        this.welcome = m;
        this.state = m.state;
        this.sessionId = m.session_id;
        break;
      case "state":
        this.state = m.state;
        break;
      case "session.started":
        this.sessionId = m.session_id;
        this.sessionAudio = m.audio;
        if (this.claiming) {
          this.claiming = false;
          this.ownSessionId = m.session_id;
        }
        break;
      case "session.ended": {
        const mine = m.session_id === this.ownSessionId;
        const textOnly = this.sessionAudio === "none";
        if (mine) this.ownSessionId = null;
        // A late end of a session already replaced (a take-over from the PC) must not stop
        // the one now running.
        if (this.sessionId !== null && m.session_id !== this.sessionId) break;
        this.sessionId = null;
        this.sessionAudio = null;
        this.state = "idle";
        this.pausedReply = null;
        // While this client's `session.start` is in flight, the session ending is the one it
        // takes over (the host ends the PC's before starting ours): our mic is already open
        // and must stay so, or the new session listens to nothing. The same for a text-only
        // session of this client's own (typed, no mic) that its new voice session replaces.
        if (this.claiming && (!mine || textOnly)) this.stopOutput();
        else this.stopAudio();
        break;
      }
      case "tts.chunk":
        this.chunkedReply = m.reply_id;
        // Sessions whose audio isn't played here: time the kit's own playback.
        if (this.sessionAudio !== "client") this.clock.chunk(m.reply_id, m.ms);
        break;
      case "reply.started":
        this.openReplies.add(m.reply_id);
        break;
      case "conversation.recent":
        // What a phone missed while away (1.11): its replies still open are open here too, so a
        // later disconnect settles them like any other.
        for (const e of m.events) {
          if (e.type === "reply.started") this.openReplies.add(e.reply_id);
          else if (e.type === "reply.heard") this.openReplies.delete(e.reply_id);
        }
        break;
      case "reply.paused": {
        // Paused or resumed by any client (or the kit): this page's audio follows.
        const here = this.sessionAudio === "client" && this.player ? this.player : this.clock;
        if (m.paused) here.pause?.(m.reply_id);
        else here.resume?.(m.reply_id);
        const was = this.pausedReply;
        this.pausedReply = m.paused ? m.reply_id : was === m.reply_id ? null : was;
        // Already reported locally: not again.
        if ((was === m.reply_id) === m.paused) return;
        break;
      }
      case "reply.heard":
        if (this.chunkedReply === m.reply_id) this.chunkedReply = null;
        this.openReplies.delete(m.reply_id);
        if (this.pausedReply === m.reply_id) this.pausedReply = null;
        if (m.outcome === "silenced") {
          this.silenced.add(m.reply_id);
          if (this.silenced.size > SILENCED_MAX) this.silenced.delete(this.silenced.values().next().value!);
          this.player?.interrupt(m.reply_id);
          this.clock.interrupt(m.reply_id);
        }
        break;
      case "ack":
        this.settle(m.re);
        break;
      case "error":
        if (m.re) this.settle(m.re, new ClientError(m.code, m.message));
        break;
    }
    this.emit(m);
  }

  private audio(frame: ArrayBuffer): void {
    const a = decodeAudioDown(frame);
    // Frames of a silenced reply that were already on their way are dropped (PROTOCOL.md 6).
    if (!a || !this.player || this.silenced.has(a.replyId)) return;
    this.player.enqueue(a.replyId, a.pcm);
  }

  private emit(m: ServerMsg): void {
    for (const fn of this.listeners.get(m.type) ?? []) fn(m);
    for (const fn of this.anyListeners) fn(m);
  }

  private settle(re: string, err?: ClientError): void {
    const p = this.pending.get(re);
    if (!p) return;
    this.pending.delete(re);
    if (err) p.reject(err);
    else p.resolve();
  }

  private newEpoch(): void {
    // A new kit process: its ids start over, so nothing from the old one may be matched by id.
    this.silenced.clear();
    this.openReplies.clear();
    this.ownSessionId = null;
    this.pausedReply = null;
    this.player?.reset();
    this.clock.reset();
  }

  private connectionLost(): void {
    for (const [re] of this.pending) this.settle(re, new ClientError("disconnected", "connection to the voice kit lost"));
    this.stopAudio();
    this.sessionId = null;
    this.sessionAudio = null;
    this.ownSessionId = null;
    this.claiming = false;
    this.state = "idle";
    // A paused reply can't be carried on: its rest is marked unspoken below, like any open one.
    this.pausedReply = null;
    // Nothing more will arrive for what was in flight: views drop the half-heard partial and
    // mark the open reply unspoken (local events, the same ones the kit would send).
    this.emit({ type: "stt.partial", text: "" });
    const open = [...this.openReplies];
    this.openReplies.clear();
    for (const id of open) {
      this.emit({ type: "reply.heard", reply_id: id, outcome: "unspoken", heard: "", spoken_so_far: "", cause: "disconnected" });
    }
  }

  private stopAudio(): void {
    this.micLive = false;
    this.capture?.stop();
    this.stopOutput();
  }

  private stopOutput(): void {
    this.player?.stopAll();
    this.clock.stopAll();
  }
}

/** Standard base64 of `bytes`. */
export function base64(bytes: Uint8Array): string {
  let s = "";
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

/** How many silenced reply ids are remembered (late frames come within moments). */
const SILENCED_MAX = 256;

/** A failed call: `code` is the protocol's error code (`no_session`, `session_active`, ...). */
export class ClientError extends Error {
  constructor(readonly code: string, message: string) {
    super(message);
    this.name = "ClientError";
  }
}
